// eslint-disable-next-line no-restricted-imports
import { Canvas } from '@react-three/fiber';
import {
  ComponentProps,
  ForwardedRef,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
} from 'react';

import { ErrorBoundary } from '@sorare/error-boundary';

import { sendSafeError } from '@sorare/core/src/lib/error';

function useMergedRef<T>(forwardedRef: ForwardedRef<T>) {
  const mutableRef = useRef<T | null>(null);

  const ref = (instance: T | null) => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(instance);
    } else if (forwardedRef) {
      forwardedRef.current = instance;
    }
    mutableRef.current = instance;
  };

  return { ref, mutableRef };
}

export const SafeCanvas = forwardRef(function SafeCanvas(
  props: ComponentProps<typeof Canvas> & {
    onContextLost?: () => void;
    onContextRestored?: () => void;
    fallback: ReactNode;
  },
  forwardedRef: ForwardedRef<HTMLCanvasElement>
) {
  const { mutableRef: canvasRef, ref } =
    useMergedRef<HTMLCanvasElement>(forwardedRef);

  const { onContextLost, onContextRestored, fallback, ...rest } = props;

  useEffect(() => {
    if (!canvasRef.current) {
      return () => {};
    }

    const handleContextLost = () => {
      if (onContextLost) {
        onContextLost();
        return;
      }
      sendSafeError('WebGL context lost');
    };

    const handleContextRestored = () => {
      onContextRestored?.();
    };

    const canvas = canvasRef.current;
    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);
    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      canvas.removeEventListener('webglcontextlost', handleContextRestored);
    };
  });

  return (
    <ErrorBoundary
      fallback={fallback}
      doNotReportToSentry={e => e.message === 'Error creating WebGL context.'}
    >
      <Canvas ref={ref} dpr={[1, 2]} {...rest} fallback={fallback} />
    </ErrorBoundary>
  );
});
